import { GroupIconPicker } from '@/components/shared/GroupIconPicker';
import type { EventChallengeGroup, GroupUnlockMode } from '@/config/objects/events';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

interface EventGroupEditDialogProps {
  eventId: string;
  group?: EventChallengeGroup | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
}

const UNLOCK_MODE_OPTIONS: { value: GroupUnlockMode; label: string; description: string }[] = [
  {
    value: 'all_at_once',
    label: 'All at Once',
    description: 'All challenges in this group become available when the group unlocks',
  },
  {
    value: 'sequential',
    label: 'Sequential',
    description: 'Challenges unlock one-by-one as each is completed',
  },
];

const COLOR_OPTIONS = [
  { value: 'blue', label: 'Blue', className: 'bg-blue-500' },
  { value: 'green', label: 'Green', className: 'bg-green-500' },
  { value: 'purple', label: 'Purple', className: 'bg-purple-500' },
  { value: 'orange', label: 'Orange', className: 'bg-orange-500' },
  { value: 'pink', label: 'Pink', className: 'bg-pink-500' },
  { value: 'teal', label: 'Teal', className: 'bg-teal-500' },
];

export function EventGroupEditDialog({
  eventId,
  group,
  open,
  onOpenChange,
  disabled = false,
}: EventGroupEditDialogProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const isEditing = !!group;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string>('blue');
  const [icon, setIcon] = useState<string | null>(null);
  const [unlockMode, setUnlockMode] = useState<GroupUnlockMode>('all_at_once');

  // Reset form when dialog opens/closes or group changes
  useEffect(() => {
    if (open) {
      if (group) {
        setName(group.name);
        setDescription(group.description || '');
        setColor(group.color || 'blue');
        setIcon(group.icon ?? null);
        setUnlockMode(group.unlockMode);
      } else {
        setName('');
        setDescription('');
        setColor('blue');
        setIcon(null);
        setUnlockMode('all_at_once');
      }
    }
  }, [open, group]);

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; description?: string; color?: string; icon?: string | null; unlockMode: GroupUnlockMode }) => {
      return apiClient.post(`/events/${eventId}/groups`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['EventGroups', eventId] });
      queryClient.invalidateQueries({ queryKey: ['EventChallenges', eventId] });
      toast.success('Group created');
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to create group');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { name?: string; description?: string; color?: string; icon?: string | null; unlockMode?: GroupUnlockMode }) => {
      return apiClient.patch(`/events/${eventId}/groups/${group!.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['EventGroups', eventId] });
      queryClient.invalidateQueries({ queryKey: ['EventChallenges', eventId] });
      toast.success('Group updated');
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to update group');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data = {
      name: name.trim(),
      description: description.trim() || undefined,
      color,
      // `null` clears the icon back to the mobile default; the API field
      // is nullable so both create and PATCH accept it.
      icon,
      unlockMode,
    };

    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Group' : 'Create Group'}</DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Update the group settings'
                : 'Create a new challenge group for this event'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Starter Challenges"
                required
                disabled={disabled}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this group contains..."
                rows={2}
                disabled={disabled}
              />
            </div>

            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex gap-2">
                {COLOR_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setColor(option.value)}
                    disabled={disabled}
                    className={`w-8 h-8 rounded-full ${option.className} ${
                      color === option.value
                        ? 'ring-2 ring-offset-2 ring-primary'
                        : 'hover:opacity-80'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={option.label}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Icon</Label>
              <GroupIconPicker value={icon} onChange={setIcon} disabled={disabled} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="unlockMode">Unlock Mode</Label>
              <Select
                value={unlockMode}
                onValueChange={(v) => setUnlockMode(v as GroupUnlockMode)}
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNLOCK_MODE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {UNLOCK_MODE_OPTIONS.find((o) => o.value === unlockMode)?.description}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || isPending || disabled}>
              {isPending ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Group'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
